/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useState, useMemo } from 'react'
import {
  Avatar,
  Button,
  Modal,
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Tooltip,
  Badge,
  LinearProgress,
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  Download,
  Filter,
  RefreshCw,
  Search,
  Eye,
  Heart,
  MessageCircle,
  Users,
  Image as ImageIcon,
  BarChart3,
  Calendar,
  X,
  ExternalLink,
  Zap,
  Star,
  Target,
  Send,
  Copy,
  UserPlus,
} from 'lucide-react'
import Spinner from 'components/Spinner/Spinner'
import { Colors } from 'constants/colors'
import { format } from 'date-fns'
import {
  useAnalyzeFollowersMutation,
  useCreateInstagramInsightsMutation,
  useGetInstagramInsightsQuery,
  useGetTargetFollowersQuery,
  useGetTargetResultsQuery,
  useRefreshTargetFollowersMutation,
  useFetchFollowerDetailsMutation,
  useFetchSimilarAccountsMutation,
  useGenerateProfileSummaryMutation, 
  useGenerateCommentsSummaryMutation, 
} from 'graphql/generated/graphql'
import AnalyzeFollowersInitial from './components/AnalyzeFollowersInitial'
import InstagramInsights from './components/InstagramInsights'
import axios from 'axios'
import { NalapodFollowerSegmentation } from './components/NalapodFollowerSegmentation'


interface TagType {
  name: string
  count: number
}

interface FollowerType {
  username: string
  platform: string
  bio: string
  profileImage: string
  tags: TagType[]
  profileSummary: string
  commentsSummary: string
  stats: {
    followers: string
    posts: string
    following: string
    likes: number
    comments: number
  }
  engagementRate?: number
  avgLikesPerPost?: number
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`follower-tabpanel-${index}`}
      aria-labelledby={`follower-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

const ModernFollowerCard = React.memo(function ModernFollowerCard({
  follower,
  rank,
}: {
  follower: FollowerType
  rank: number
}) {
  const avgLikesPerPost = follower.stats.likes / 12
  const avgCommentsPerPost = follower.stats.comments / 12
  const engagementRate = (
    ((avgLikesPerPost + avgCommentsPerPost) / parseInt(follower.stats.followers)) * 100
  ).toFixed(2)

  const handleCopyUsername = () => {
    navigator.clipboard.writeText(follower.username).catch(console.error)
  }

  const handleDM = () => {
    window.open(`https://instagram.com/direct/t/${follower.username}`, '_blank')
  }

  const handleViewProfile = () => {
    window.open(`https://instagram.com/${follower.username}`, '_blank')
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(88, 28, 135, 0.3) 100%)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        padding: '24px',
        marginBottom: '20px',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(168, 85, 247, 0.3)',
          border: '1px solid rgba(168, 85, 247, 0.4)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover::before': {
          opacity: 1,
        },
      }}
    >
      {/* Rank Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          borderRadius: '12px',
          padding: '6px 12px',
          fontWeight: 700,
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Star size={16} fill="white" />
        #{rank}
      </Box>

      <Box sx={{ display: 'flex', gap: '24px', flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {/* Left Section - Profile */}
        <Box sx={{ flex: '0 0 320px', minWidth: '280px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', mb: 3 }}>
            <Avatar
              src={follower.profileImage}
              alt={follower.username}
              sx={{
                width: 80,
                height: 80,
                border: '3px solid rgba(168, 85, 247, 0.4)',
                boxShadow: '0 4px 20px rgba(168, 85, 247, 0.3)',
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '18px',
                  }}
                >
                  @{follower.username}
                </Typography>
                <IconButton
                  size="small"
                  href={`https://instagram.com/${follower.username}`}
                  target="_blank"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    '&:hover': { color: '#a855f7' },
                  }}
                >
                  <ExternalLink size={16} />
                </IconButton>
              </Box>
              <Chip
                label={follower.platform}
                size="small"
                sx={{
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: '#c084fc',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  fontSize: '11px',
                  height: '22px',
                }}
              />
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '13px',
                  mt: 1,
                  lineHeight: 1.4,
                }}
              >
                {follower.bio || 'No bio available'}
              </Typography>
            </Box>
          </Box>

          {/* Engagement Rate Highlight */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
              borderRadius: '12px',
              padding: '12px',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              mb: 2,
            }}
          >
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', mb: '4px' }}>
              Engagement Rate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={20} style={{ color: '#ec4899' }} />
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {engagementRate}%
              </Typography>
            </Box>
          </Box>

          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {follower.tags
              .filter(tag => tag.count > 0)  // ✅ Only show hashtags with counts
              .slice(0, 4)
              .map((tag, idx) => (
                <Chip
                  key={idx}
                  label={`${tag.name} (${tag.count})`}
                  size="small"
                  sx={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    fontSize: '11px',
                    '&:hover': {
                      background: 'rgba(168, 85, 247, 0.2)',
                      border: '1px solid rgba(168, 85, 247, 0.4)',
                    },
                  }}
                />
              ))}
            {/* ✅ Show message if no hashtags */}
            {follower.tags.filter(tag => tag.count > 0).length === 0 && (
              <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                No hashtags found
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Section - Insights & Stats */}
        <Box sx={{ flex: 1, minWidth: '300px' }}>
          {/* AI Summaries */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: '#c084fc',
                fontSize: '13px',
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Eye size={14} />
              Profile Insights
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '13px',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              {follower.profileSummary || 'Analyzing profile...'}
            </Typography>

            <Typography
              sx={{
                color: '#c084fc',
                fontSize: '13px',
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <MessageCircle size={14} />
              Engagement Insights
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '13px',
                lineHeight: 1.6,
              }}
            >
              {follower.commentsSummary || 'Analyzing engagement...'}
            </Typography>
          </Box>

          {/* Stats Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '12px',
              pt: 2,
              borderTop: '1px solid rgba(168, 85, 247, 0.2)',
            }}
          >
            {[
              { label: 'Followers', value: follower.stats.followers, icon: Users },
              { label: 'Posts', value: follower.stats.posts, icon: ImageIcon },
              { label: 'Following', value: follower.stats.following, icon: Users },
              { 
                label: 'Total Likes', // ✅ Changed from 'Likes'
                value: follower.stats.likes.toLocaleString(), 
                icon: Heart,
                tooltip: 'Total likes from last 12 posts'  // ✅ Added tooltip
              },
              { 
                label: 'Total Comments', // ✅ Changed from 'Comments'
                value: follower.stats.comments.toLocaleString(), 
                icon: MessageCircle,
                tooltip: 'Total comments from last 12 posts'  // ✅ Added tooltip
              },
            ].map((stat, idx) => (
              <Tooltip 
                key={idx} 
                title={stat.tooltip ?? ''}  // ✅ Add tooltip
                placement="top"
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: '4px' }}>
                    <stat.icon size={16} style={{ color: 'rgba(168, 85, 247, 0.6)' }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: 'rgba(255, 255, 255, 0.9)',
                      mb: '2px',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/* Action Items */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: '1px solid rgba(168, 85, 247, 0.2)',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <Tooltip title="Send a DM on Instagram">
              <Button
                variant="contained"
                size="small"
                startIcon={<Send size={16} />}
                onClick={handleDM}
                sx={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1,
                  minWidth: '120px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                  },
                }}
              >
                Send DM
              </Button>
            </Tooltip>
            <Tooltip title="View Instagram profile">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExternalLink size={16} />}
                onClick={handleViewProfile}
                sx={{
                  borderColor: 'rgba(168, 85, 247, 0.5)',
                  color: '#c084fc',
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1,
                  minWidth: '120px',
                  '&:hover': {
                    borderColor: '#a855f7',
                    background: 'rgba(168, 85, 247, 0.1)',
                  },
                }}
              >
                View Profile
              </Button>
            </Tooltip>
            <Tooltip title="Copy username">
              <IconButton
                size="small"
                onClick={handleCopyUsername}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  '&:hover': {
                    color: '#a855f7',
                    background: 'rgba(168, 85, 247, 0.1)',
                    borderColor: '#a855f7',
                  },
                }}
              >
                <Copy size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  )
})

export default function ModernAnalyzeFollowers(): JSX.Element {
  const [error, setError] = useState<string | null>(null)
  const [followersListData, setFollowersData] = useState<FollowerType[]>([])
  const [followersListActiveData, setFollowersActiveData] = useState<FollowerType[]>([])
  const [followersListSimilarData, setFollowersListSimilarData] = useState<FollowerType[]>([])
  const [loadingList, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'engagement' | 'followers' | 'likes'>('followers')
  const [open, setOpen] = useState(false)
  const [openRefresh, setOpenRefresh] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // GraphQL hooks
  const [analyzeFollowers, { loading }] = useAnalyzeFollowersMutation()
  const [refreshTargetFollowers, { loading: refreshLoading }] = useRefreshTargetFollowersMutation()
  const [createInsights, { loading: insightsLoading }] = useCreateInstagramInsightsMutation()
  const {
    data,
    loading: followersLoading,
    refetch: dataRefetch,
  } = useGetTargetFollowersQuery({ fetchPolicy: 'network-only' })
  const {
    data: insightsData,
    loading: getInsightsLoading,
    refetch,
  } = useGetInstagramInsightsQuery()
  const { data: followersData, loading: resultsLoading } = useGetTargetResultsQuery()
  const [fetchFollowerDetails] = useFetchFollowerDetailsMutation()
  const [fetchSimilarAccounts] = useFetchSimilarAccountsMutation()
  const [generateProfileSummary] = useGenerateProfileSummaryMutation()
  const [generateCommentsSummary] = useGenerateCommentsSummaryMutation()
  
  // ✅ NEW: Load data from database on mount
  useEffect(() => {
    if (data?.getTargetFollowers && followersData?.getTargetFollowersResults) {
      void loadDataFromDatabase()
    }
  }, [data?.getTargetFollowers, followersData?.getTargetFollowersResults])
  // ✅ NEW: Poll for processing status
useEffect(() => {
  let pollInterval: NodeJS.Timeout | null = null
  
  if (data?.getTargetFollowers?.isProcessing) {
    console.log(`📊 Polling status: ${data.getTargetFollowers.processingStatus} (${data.getTargetFollowers.progress}%)`)
    
    // Poll every 3 seconds
    pollInterval = setInterval(() => {
      void dataRefetch()
    }, 3000)
  } else if (data?.getTargetFollowers && !data.getTargetFollowers.isProcessing) {
    // Processing complete - load data
    void loadDataFromDatabase()
  }
  
  return () => {
    if (pollInterval) {
      clearInterval(pollInterval)
    }
  }
}, [data?.getTargetFollowers?.isProcessing, data?.getTargetFollowers?.progress, dataRefetch])
const handleTabChange = (newValue: number) => {
  setActiveTab(newValue)
  // Auto-set appropriate sort for each tab
  if (newValue === 0) {
    setSortBy('followers') // Similar Accounts - sort by followers
  } else if (newValue === 1) {
    setSortBy('followers') // Most Popular - sort by followers
  } else {
    setSortBy('engagement') // Most Active - sort by engagement
  }
}
  // ✅ NEW: Function to load data from database
  const loadDataFromDatabase = async () => {
    if (!data?.getTargetFollowers || !followersData?.getTargetFollowersResults) return

    const accountName = data.getTargetFollowers.accountName
    const popularUsernames = (data.getTargetFollowers.popularFollowers ?? []).map((u: any) => u.username)
    const activeUsernames = (data.getTargetFollowers.activeFollowers ?? []).map((u: any) => u.username)
    const similarUsernames = (data.getTargetFollowers.similarFollowers ?? []).map((u: any) => u.username)

    // Map database results to FollowerType format
    const dbFollowers = followersData.getTargetFollowersResults.map((item: any) => ({
      username: item.username,
      platform: 'Instagram',
      bio: item.summary || '',
      profileImage: item.avatarUrl,
      tags: (item.hashtags || []).map((h: string) => ({ name: h, count: 0 })),
      profileSummary: item.summary || '',
      commentsSummary: '',
      stats: {
        followers: item.followersCount?.toString() || '0',
        posts: '0',
        following: '0',
        likes: item.likesCount || 0,
        comments: item.commentsCount || 0,
      },
    }))

    // Separate by category
    const popularFollowers = dbFollowers.filter((f: any) => 
      popularUsernames.includes(f.username) && f.username !== accountName
    )
    const activeFollowers = dbFollowers.filter((f: any) => 
      activeUsernames.includes(f.username) && f.username !== accountName
    )
    
    // For similar accounts, we need to fetch them fresh since they're not in targetFollowersResult
    const similarFollowersData = await fetchSimilarAccountsData(similarUsernames, accountName)

    setFollowersData(popularFollowers)
    setFollowersActiveData(activeFollowers)
    setFollowersListSimilarData(similarFollowersData)

    // Generate AI summaries if not already present
    if (popularFollowers.length > 0 && !popularFollowers[0].profileSummary) {
      await fetchProfileSummaryPopular(popularFollowers)
    }
    if (activeFollowers.length > 0 && !activeFollowers[0].profileSummary) {
      await fetchProfileSummaryActive(activeFollowers)
    }
    if (similarFollowersData.length > 0) {
      await fetchProfileSummarySimilar(similarFollowersData)
    }
  }

  // ✅ NEW: Fetch similar accounts details
  const fetchSimilarAccountsData = async (similarUsernames: string[], accountName: string) => {
    try {
      if (similarUsernames.length === 0) return []

      const { data: followerData } = await fetchFollowerDetails({
        variables: { usernames: similarUsernames }
      })

      return (followerData?.fetchFollowerDetails ?? [])
        .filter(f => f.username !== accountName)
        .map((f: any) => ({
          username: f.username,
          platform: f.platform,
          bio: f.bio ?? '',
          profileImage: f.profileImage ?? '',
          tags: f.tags ?? [],
          profileSummary: f.profileSummary ?? '',
          commentsSummary: f.commentsSummary ?? '',
          stats: {
            followers: f.stats.followers,
            posts: f.stats.posts,
            following: f.stats.following,
            likes: f.stats.likes,
            comments: f.stats.comments,
          },
        }))
    } catch (error) {
      console.error('Error fetching similar accounts data:', error)
      return []
    }
  }

  // Filter and sort followers
  const getFilteredAndSortedFollowers = (followers: FollowerType[]) => {
    let filtered = followers

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (f) =>
          f.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
          f.bio.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Sort
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'engagement': {
          const aRate = (a.stats.likes + a.stats.comments) / parseInt(a.stats.followers)
          const bRate = (b.stats.likes + b.stats.comments) / parseInt(b.stats.followers)
          return bRate - aRate
        }
        case 'followers':
          return parseInt(b.stats.followers) - parseInt(a.stats.followers)
        case 'likes':
          return b.stats.likes - a.stats.likes
        default:
          return 0
      }
    })
  }

  const filteredSimilar = useMemo(
    () => getFilteredAndSortedFollowers(followersListSimilarData),
    [followersListSimilarData, searchQuery, sortBy]
  )
  const filteredPopular = useMemo(
    () => getFilteredAndSortedFollowers(followersListData),
    [followersListData, searchQuery, sortBy]
  )
  const filteredActive = useMemo(
    () => getFilteredAndSortedFollowers(followersListActiveData),
    [followersListActiveData, searchQuery, sortBy]
  )

  // Export functionality
  const exportToCSV = () => {
    const allFollowers = [...followersListSimilarData, ...followersListData, ...followersListActiveData]
    const csv = [
      ['Username', 'Platform', 'Followers', 'Posts', 'Likes', 'Comments', 'Engagement Rate', 'Bio'].join(','),
      ...allFollowers.map((f) => {
        const engagementRate = ((f.stats.likes + f.stats.comments) / parseInt(f.stats.followers) * 100).toFixed(2)
        return [
          f.username,
          f.platform,
          f.stats.followers,
          f.stats.posts,
          f.stats.likes,
          f.stats.comments,
          engagementRate,
          `"${f.bio.replace(/"/g, '""')}"`,
        ].join(',')
      }),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `nalapod-followers-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
  }

  // ✅ UPDATED: Manual refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true)
    setError(null)
    
    try {
      if (data?.getTargetFollowers?.account_id && data.getTargetFollowers.id) {
        await refreshTargetFollowers({
          variables: {
            accountId: data.getTargetFollowers.account_id,
            id: data.getTargetFollowers.id,
          },
        })
        await dataRefetch()
        setOpenRefresh(false)
        // Reload data after refresh
        setTimeout(() => {
          void loadDataFromDatabase()
        }, 1000)
      }
    } catch (e) {
      console.error(e)
      setError('Failed to refresh data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const fetchProfileSummaryPopular = async (data: FollowerType[]) => {
    try {
      const summaryPromises = data.map(async (follower) => {
        const { data: profileData } = await generateProfileSummary({
          variables: {
            username: follower.username,
            stats: {
              followers: follower.stats.followers,
              posts: follower.stats.posts,
              following: follower.stats.following,
              likes: follower.stats.likes,
              comments: follower.stats.comments,
            },
          },
        })
        
        const { data: commentsData } = await generateCommentsSummary({
          variables: {
            username: follower.username,
            commentCount: follower.stats.comments,
          },
        })
  
        return {
          ...follower,
          profileSummary: profileData?.generateProfileSummary ?? '',
          commentsSummary: commentsData?.generateCommentsSummary ?? '',
        }
      })
      const updated = await Promise.all(summaryPromises)
      setFollowersData(updated)
    } catch (error) {
      console.error('Error fetching summaries:', error)
    }
  }
  
  const fetchProfileSummaryActive = async (data: FollowerType[]) => {
    try {
      const summaryPromises = data.map(async (follower) => {
        const { data: profileData } = await generateProfileSummary({
          variables: {
            username: follower.username,
            stats: {
              followers: follower.stats.followers,
              posts: follower.stats.posts,
              following: follower.stats.following,
              likes: follower.stats.likes,
              comments: follower.stats.comments,
            },
          },
        })
        
        const { data: commentsData } = await generateCommentsSummary({
          variables: {
            username: follower.username,
            commentCount: follower.stats.comments,
          },
        })
  
        return {
          ...follower,
          profileSummary: profileData?.generateProfileSummary ?? '',
          commentsSummary: commentsData?.generateCommentsSummary ?? '',
        }
      })
      const updated = await Promise.all(summaryPromises)
      setFollowersActiveData(updated)
    } catch (error) {
      console.error('Error fetching summaries:', error)
    }
  }
  
  const fetchProfileSummarySimilar = async (data: FollowerType[]) => {
    try {
      const summaryPromises = data.map(async (follower) => {
        const { data: profileData } = await generateProfileSummary({
          variables: {
            username: follower.username,
            stats: {
              followers: follower.stats.followers,
              posts: follower.stats.posts,
              following: follower.stats.following,
              likes: follower.stats.likes,
              comments: follower.stats.comments,
            },
          },
        })
        
        const { data: commentsData } = await generateCommentsSummary({
          variables: {
            username: follower.username,
            commentCount: follower.stats.comments,
          },
        })
  
        return {
          ...follower,
          profileSummary: profileData?.generateProfileSummary ?? '',
          commentsSummary: commentsData?.generateCommentsSummary ?? '',
        }
      })
      const updated = await Promise.all(summaryPromises)
      setFollowersListSimilarData(updated)
    } catch (error) {
      console.error('Error fetching summaries:', error)
    }
  }

  if (loading || resultsLoading || insightsLoading || getInsightsLoading || followersLoading || refreshLoading) {
    return <Spinner />
  }

  if (!data?.getTargetFollowers && !loading && !followersLoading && !resultsLoading) {
    return <AnalyzeFollowersInitial analyzeFollowers={analyzeFollowers} refetch={dataRefetch} />
  }
  // ✅ NEW: Show processing status
if (data?.getTargetFollowers?.isProcessing) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        padding: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(88, 28, 135, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          padding: '48px',
          maxWidth: '600px',
          width: '90%',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Analyzing Your Followers
        </Typography>
        
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4 }}>
          {data.getTargetFollowers.processingStatus.replace(/_/g, ' ')}...
        </Typography>
        
        <LinearProgress
          variant="determinate"
          value={data.getTargetFollowers.progress}
          sx={{
            height: '8px',
            borderRadius: '4px',
            backgroundColor: 'rgba(168, 85, 247, 0.2)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
              borderRadius: '4px',
            },
          }}
        />
        
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 2, fontSize: '14px' }}>
          {data.getTargetFollowers.progress}% complete
        </Typography>
        
        {data.getTargetFollowers.errorMessage && (
          <Typography sx={{ color: '#ef4444', mt: 3 }}>
            Error: {data.getTargetFollowers.errorMessage}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        padding: '40px',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Follower Analytics
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
          Deep insights into your most engaged Instagram followers
        </Typography>
      </Box>

      {/* Account Info Card */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(88, 28, 135, 0.3) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          padding: '24px',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <Avatar
            src={data?.getTargetFollowers?.avatarUrl}
            sx={{
              width: 64,
              height: 64,
              border: '3px solid rgba(168, 85, 247, 0.4)',
              boxShadow: '0 4px 20px rgba(168, 85, 247, 0.3)',
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: '4px' }}>
              @{data?.getTargetFollowers?.accountName}
            </Typography>
            {insightsData?.getInstagramInsights?.startDate && insightsData.getInstagramInsights.endDate && (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                {`${format(new Date(insightsData.getInstagramInsights.startDate), 'd MMM')} - 
                  ${format(new Date(insightsData.getInstagramInsights.endDate), 'd MMM')}`}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
            Last updated: {format(new Date(data?.getTargetFollowers?.resultsDate ?? Date.now()), 'd MMM y HH:mm')}
          </Typography>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => setOpenRefresh(true)}
              disabled={isRefreshing}
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: '#a855f7',
                },
              }}
            >
              <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton
              onClick={exportToCSV}
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: '#a855f7',
                },
              }}
            >
              <Download size={20} />
            </IconButton>
          </Tooltip>
          {!insightsData?.getInstagramInsights && (
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                },
              }}
            >
              Get Insights
            </Button>
          )}
        </Box>
      </Box>

      {/* Insights Dashboard */}
      {insightsData?.getInstagramInsights && (
        <Box sx={{ mb: 3 }}>
          <InstagramInsights data={insightsData.getInstagramInsights} />
        </Box>
      )}

      {/* Follower Segmentation */}
      {(followersListSimilarData.length > 0 || 
        followersListData.length > 0 || 
        followersListActiveData.length > 0) && (
        <Box sx={{ mb: 3 }}>
          <NalapodFollowerSegmentation 
            followers={(() => {
              const all = [
                ...followersListSimilarData,
                ...followersListData,
                ...followersListActiveData
              ]
              // Deduplicate
              return all.filter(
                (f, i, self) => i === self.findIndex(x => x.username === f.username)
              )
            })()} 
          />
        </Box>
      )}

      {/* Controls Bar */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(88, 28, 135, 0.3) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          padding: '16px 24px',
          mb: 3,
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search followers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: <Search size={18} style={{ marginRight: '8px', color: 'rgba(255, 255, 255, 0.4)' }} />,
          }}
          sx={{
            flex: 1,
            minWidth: '250px',
            '& .MuiOutlinedInput-root': {
              color: 'white',
              background: 'rgba(15, 23, 42, 0.5)',
              '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#a855f7' },
            },
          }}
        />
        <TextField
          select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          size="small"
          sx={{
            minWidth: '180px',
            '& .MuiOutlinedInput-root': {
              color: 'white',
              background: 'rgba(15, 23, 42, 0.5)',
              '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#a855f7' },
            },
          }}
        >
          <MenuItem value="engagement">Sort by Engagement</MenuItem>
          <MenuItem value="followers">Sort by Followers</MenuItem>
          <MenuItem value="likes">Sort by Likes</MenuItem>
        </TextField>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => handleTabChange(newValue)}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': {
            background: 'linear-gradient(90deg, #a855f7 0%, #ec4899 100%)',
            height: '3px',
          },
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '15px',
            '&.Mui-selected': {
              color: '#c084fc',
            },
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} />
              Similar Accounts ({filteredSimilar.length})
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={18} />
              Most Popular ({filteredPopular.length})
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={18} />
              Most Active ({filteredActive.length})
            </Box>
          }
        />
      </Tabs>

      {/* Content */}
      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#a855f7' }} />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
          {error}
        </Typography>
      ) : (
        <>
          <TabPanel value={activeTab} index={0}>
            {filteredSimilar.length > 0 ? (
              filteredSimilar.map((follower, index) => (
                <ModernFollowerCard key={follower.username} follower={follower} rank={index + 1} />
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 4 }}>
                No similar accounts found
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {filteredPopular.length > 0 ? (
              filteredPopular.map((follower, index) => (
                <ModernFollowerCard key={follower.username} follower={follower} rank={index + 1} />
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 4 }}>
                No popular followers found
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            {filteredActive.length > 0 ? (
              filteredActive.map((follower, index) => (
                <ModernFollowerCard key={follower.username} follower={follower} rank={index + 1} />
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 4 }}>
                No active followers found
              </Typography>
            )}
          </TabPanel>
        </>
      )}

      {/* Get Insights Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(88, 28, 135, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Get Daily Instagram Insights
            </Typography>
            <IconButton onClick={() => setOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              <X size={20} />
            </IconButton>
          </Box>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, lineHeight: 1.6 }}>
            Track your Instagram performance effortlessly with daily insights. Get impressions, reach, and profile views
            data every 24 hours. Optimize your strategy and drive results with confidence.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setOpen(false)
              createInsights({
                variables: {
                  accountId: data?.getTargetFollowers?.account_id ?? 0,
                },
              })
                .then(() => {
                  void refetch()
                })
                .catch(console.error)
            }}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              padding: '12px',
              fontSize: '15px',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              },
            }}
          >
            Confirm & Get Insights
          </Button>
        </Box>
      </Modal>

      {/* Refresh Modal */}
      <Modal open={openRefresh} onClose={() => setOpenRefresh(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(88, 28, 135, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Refresh Follower Insights
            </Typography>
            <IconButton onClick={() => setOpenRefresh(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              <X size={20} />
            </IconButton>
          </Box>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, lineHeight: 1.6 }}>
            Are you sure you want to refresh your follower insights? This will update data on your most active and
            popular followers, and similar accounts.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => { void handleRefresh() }}
            disabled={isRefreshing}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              padding: '12px',
              fontSize: '15px',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              },
            }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>
      </Modal>
    </Box>
  )
}